import { Box, Flex, Icon, Input, SimpleGrid, Text, Textarea } from "@chakra-ui/react";
import { FormikProps } from "formik";
import Select from "react-select";
import { IUpdateCandidateBody } from "../../../api/candidate/types";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { useEffect, useState } from "react";
import { transformGuides } from "../../../helpers/transformGuides";
import { BaseInput } from "../../Controls/BaseInput";
import FileUploader from "../../FileUploader/FileUploader";
import { fetchCities } from "../../../store/slices/guides";
import { AddSkillsModal } from "../CreateCandidateBlock/AddSkillsModal";
import React from "react";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { FiDatabase, FiDollarSign, FiFile, FiMapPin, FiMessageCircle, FiUser } from "react-icons/fi";
import { UserRole } from "@/api/auth/types";
import { selectUserRole } from "@/store/slices/user";
import api from "@/api";
import { fetchAllDepartments } from "@/store/slices/departments";

const colourStyles = {
  control: (styles: any) => ({
    ...styles,
    maxWidth: "100%",
    borderColor: "grey.200",
  }),
};

interface UpdateCandidateBlockProps {
  formik: FormikProps<IUpdateCandidateBody>;
}

export const UpdateCandidateBlock = React.memo(({ formik }: UpdateCandidateBlockProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const dispatch = useAppDispatch();

  const { departments } = useAppSelector((state: RootState) => state.departments);

  const role = useAppSelector(selectUserRole);
  const isAdmin = role === UserRole.ADMIN;

  const { currentCandidate } = useAppSelector((state: RootState) => state.candidates);

  const [grageList, setGrageList] = useState<{ value: string | undefined; label: string | undefined }[]>([]);
  const [countryList, setCountryList] = useState<{ value: string | undefined; label: string | undefined }[]>([]);
  const [townList, setTownList] = useState<{ value: string; label: string }[]>([]);
  const [sourceList, setSourceList] = useState<{ value: string | undefined; label: string | undefined }[]>([]);
  const [skillList, setSkillList] = useState<{ value: string | undefined; label: string | undefined }[]>([]);
  const [currencyList, setCurrencyList] = useState<{ value: string | undefined; label: string | undefined }[]>([]);

  const [channelList, setChannelList] = useState<{ value: number | undefined; label: string | undefined }[]>([]);

  const [departmentList, setDepartmentList] = useState<{ value: number | undefined; label: string | undefined }[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (guides) {
      const resultGrageList = transformGuides(guides.Grade);
      setGrageList(resultGrageList);
      const resultCountryList = transformGuides(guides.Country);
      setCountryList(resultCountryList);
      const resultSkillList = transformGuides(guides.Skill);
      setSkillList(resultSkillList);
      const resultSourceList = transformGuides(guides.CandidateSource);
      setSourceList(resultSourceList);
      const resultCurrencyList = transformGuides(guides.Currency);
      setCurrencyList(resultCurrencyList);
    }
  }, [guides]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data } = await api.channel.getChannels();
      const channels = data.map((channel) => ({
        value: channel.id,
        label: channel.name,
      })) as { value: number; label: string }[];
      setChannelList(channels);
    })();
  }, []);

  useEffect(() => {
    dispatch(fetchAllDepartments(formik.values.channel_id ?? undefined));
  }, [formik.values.channel_id, dispatch]);

  useEffect(() => {
    if (departments) {
      setDepartmentList(
        departments.items.map((department) => ({
          value: department.id,
          label: department.name,
        })),
      );
    }
  }, [departments]);

  const [currentFiles, setCurrentFiles] = useState<any>([]);

  const [selectedSkills, setSelectedSkills] = useState<{ value: string | undefined; label: string | undefined }[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newSkills, setNewSkills] = useState<string[]>([]);

  useEffect(() => {
    // Преобразуйте skills_ids из Formik в объекты для react-select при монтировании компонента
    if (formik.values.skills_ids) {
      const initialSkills = skillList.filter((skill) => formik.values.skills_ids.includes(skill.value ?? ""));
      setSelectedSkills(initialSkills);
    }
  }, [formik.values.skills_ids, skillList]);

  type SkillOption = { value: string; label: string };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text");

    const raw = pastedText
      .split(/[\n,;\t]+/)
      .flatMap((chunk) => chunk.split(/\s{2,}/))
      .map((s) => s.trim())
      .filter(Boolean);

    const seen = new Set<string>();
    const skills = raw.filter((s) => {
      const key = s.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const existingSkills: SkillOption[] = skills
      .map((skill) => skillList.find((item) => item?.label?.toLowerCase() === skill.toLowerCase()))
      .filter((item): item is SkillOption => item !== undefined);

    const newSkillsToAdd = skills.filter((skill) => !skillList.some((item) => item?.label?.toLowerCase() === skill.toLowerCase()));

    if (newSkillsToAdd.length) {
      setNewSkills(newSkillsToAdd);
      setIsModalVisible(true);
    }

    if (existingSkills.length) {
      const incomingIds = existingSkills.map((s) => s.value);
      const currentIds: (string | number)[] = formik.values.skills_ids ?? [];
      const idsToAdd = incomingIds.filter((id) => !currentIds.includes(id));

      if (idsToAdd.length) {
        formik.setFieldValue("skills_ids", [...currentIds, ...idsToAdd]);
      }
    }
  };

  const handleModalConfirm = async (newSkills: { id: number; name: string }[]) => {
    try {
      // Добавляем новые навыки в список доступных навыков
      const newSkillOptions = newSkills.map((skill) => ({
        value: String(skill.id),
        label: skill.name,
      }));
      setSkillList((prev) => [...prev, ...newSkillOptions]);

      // Добавляем ID новых навыков в formik
      const newSkillIds = newSkills.map((skill) => String(skill.id));
      formik.setFieldValue("skills_ids", [...(formik.values.skills_ids || []), ...newSkillIds]);

      setIsModalVisible(false);
      setNewSkills([]);
    } catch (error) {
      console.error("Ошибка при обработке новых навыков:", error);
    }
  };

  const handleModalCancel = () => {
    // При отмене выбираем только существующие навыки
    const pastedText = newSkills.join("\n");
    const skills = pastedText
      .split("\n")
      .map((skill) => skill.trim())
      .filter(Boolean);

    const existingSkills = skills.filter((skill) => skillList.some((item) => item?.label?.toLowerCase() === skill.toLowerCase()));

    if (existingSkills.length > 0) {
      const existingIds = existingSkills
        .map((skill) => skillList.find((item) => item?.label?.toLowerCase() === skill.toLowerCase())?.value)
        .filter((id): id is string => id !== undefined);

      formik.setFieldValue("skills_ids", [...(formik.values.skills_ids || []), ...existingIds]);
    }

    setIsModalVisible(false);
    setNewSkills([]);
  };

  const handleSearch = async (inputValue: string) => {
    if (inputValue.length < 3) return;
    setLoading(true);
    try {
      dispatch(
        fetchCities({
          country_iso: String(formik.values.country),
          city_name: inputValue,
        }),
      ).then((response) => {
        const towns = transformGuides(response.payload).filter((town) => town.value && town.label);
        setTownList(towns);
      });
    } catch (error) {
      console.error("Ошибка при загрузке городов:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const resultFiles =
      currentCandidate?.attachments &&
      Object.entries(currentCandidate.attachments).map(([key, el]) => {
        return {
          name: el.name,
          id: key,
          isCurrentFile: true,
        };
      });
    setCurrentFiles(resultFiles);
  }, [currentCandidate]);

  return (
    <Flex flexDirection="column" gap="24px">
      <ViewInformationBlock icon={<Icon as={FiUser as any} boxSize={5} color="#3b515e" />} title="Основная информация">
        <Flex flexDirection="column" gap="16px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <BaseInput fieldTitle={"Фамилия"} requiredField={true} name="last_name" value={formik.values.last_name} onChange={formik.handleChange} />
            <Flex flexDirection="column">
              <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
                Дата рождения
              </Text>
              <Input
                h="40px"
                outline="1px solid grey.200"
                bgColor="white"
                maxW="100%"
                type="date"
                id="birthday"
                value={formik.values.birthday}
                onChange={formik.handleChange}
              />
            </Flex>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <BaseInput fieldTitle={"Имя"} requiredField={true} name="first_name" value={formik.values.first_name} onChange={formik.handleChange} />
            <BaseInput
              fieldTitle={"Должность"}
              requiredField={true}
              name="expected_position"
              value={formik.values.expected_position}
              onChange={formik.handleChange}
            />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <BaseInput fieldTitle={"Отчество"} name="patronymic_name" value={formik.values.patronymic_name} onChange={formik.handleChange} />
            <Flex flexDirection="column">
              <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
                Грейд
              </Text>
              <Select
                menuPosition="fixed"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={true}
                name="color"
                options={grageList}
                styles={colourStyles}
                placeholder="Выберите грейд"
                value={grageList.find((option) => option.value === String(formik.values.grade)) || null}
                onChange={(arg) => {
                  formik.setFieldValue("grade", arg?.value);
                }}
              />
            </Flex>
          </SimpleGrid>
          {isAdmin && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Flex flexDirection="column">
                <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
                  Канал подбора <span style={{ color: "red" }}>*</span>
                </Text>
                <Select
                  menuPosition="fixed"
                  className="basic-single"
                  classNamePrefix="select"
                  isSearchable={true}
                  name="color"
                  options={channelList}
                  styles={colourStyles}
                  placeholder="Выберите канал подбора"
                  value={channelList.find((option) => option.value === formik.values.channel_id) || null}
                  onChange={(arg) => {
                    formik.setFieldValue("channel_id", arg?.value);
                    formik.setFieldValue("department_id", null);
                  }}
                />
              </Flex>
            </SimpleGrid>
          )}
        </Flex>
      </ViewInformationBlock>
      <ViewInformationBlock icon={<Icon as={FiDollarSign as any} boxSize={5} color="#3b515e" />} title="Финансовая информация">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Flex gap="10px">
            <Box flex="1">
              <BaseInput
                fieldTitle={"Зарплата, на руки"}
                name="expected_salary"
                value={formik.values.expected_salary}
                onChange={formik.handleChange}
              />
            </Box>
            <Box flex="1" height="max-content" alignSelf="end">
              <Select
                menuPosition="fixed"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={true}
                name="color"
                options={currencyList}
                styles={{
                  ...colourStyles,
                  container: (styles: any) => ({
                    ...styles,
                    width: "100%",
                  }),
                  control: (styles: any) => ({
                    ...styles,
                    width: "100%",
                    height: "40px",
                    borderColor: "grey.200",
                  }),
                }}
                placeholder="Выберите валюту"
                value={currencyList.find((option) => option.value === String(formik.values.expected_salary_currency)) || null}
                onChange={(arg) => {
                  formik.setFieldValue("expected_salary_currency", arg?.value);
                }}
              />
            </Box>
          </Flex>
          <BaseInput
            fieldTitle={"Внутренняя ставка кандидата"}
            name="internal_rate"
            value={formik.values.internal_rate}
            onChange={formik.handleChange}
          />
        </SimpleGrid>
      </ViewInformationBlock>
      <ViewInformationBlock icon={<Icon as={FiMapPin as any} boxSize={5} color="#3b515e" />} title="Местоположение">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Flex flexDirection="column">
            <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
              Страна проживания
            </Text>
            <Select
              menuPosition="fixed"
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="color"
              options={countryList}
              styles={colourStyles}
              placeholder="Выберите страну"
              value={countryList.find((option) => option.value === String(formik.values.country)) || null}
              onChange={(arg) => {
                formik.setFieldValue("country", arg?.value);
              }}
            />
          </Flex>
          <Flex flexDirection="column">
            <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
              Город проживания
            </Text>
            <Select
              isDisabled={formik.values.country === null}
              menuPosition="fixed"
              menuPlacement="top"
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="color"
              options={townList}
              styles={colourStyles}
              placeholder="Выберите город"
              value={
                formik.values.town && !loading
                  ? {
                      value: formik.values.town,
                      label: formik.values.town,
                    }
                  : null
              }
              onChange={(arg) => {
                formik.setFieldValue("town", arg?.value);
              }}
              onInputChange={(inputValue) => {
                handleSearch(inputValue);
              }}
            />
            <Text color="gray.500" fontSize="12px">
              Введите минимум 3 символа
            </Text>
          </Flex>
        </SimpleGrid>
      </ViewInformationBlock>
      <ViewInformationBlock icon={<Icon as={FiMessageCircle as any} boxSize={5} color="#3b515e" />} title="Контактная информация">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput fieldTitle={"Email"} name="contact_email" value={formik.values.contact_email} onChange={formik.handleChange} />
          <BaseInput fieldTitle={"Телефон"} name="contact_phone" value={formik.values.contact_phone} onChange={formik.handleChange} />
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput fieldTitle={"Telegram"} name="contact_telegram" value={formik.values.contact_telegram} onChange={formik.handleChange} />
          <BaseInput fieldTitle={"Доп. контакты"} name="contacts" value={formik.values.contacts} onChange={formik.handleChange} />
        </SimpleGrid>
      </ViewInformationBlock>
      <ViewInformationBlock icon={<Icon as={FiDatabase as any} boxSize={5} color="#3b515e" />} title="Навыки и квалификация">
        <Flex flexDirection="column">
          <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
            Ключевые навыки
          </Text>
          <div onPaste={handlePaste}>
            <Select
              menuPosition="fixed"
              isMulti
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="color"
              options={skillList}
              styles={colourStyles}
              placeholder="Выберите навык"
              value={selectedSkills}
              onChange={(arg) => {
                const resultIds = arg.map((value) => value.value).filter(Boolean);
                formik.setFieldValue("skills_ids", resultIds);
              }}
            />
          </div>
        </Flex>
        <Flex flexDirection="column">
          <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
            Источник <span style={{ color: "red" }}>*</span>
          </Text>
          <Select
            menuPosition="fixed"
            className="basic-single"
            classNamePrefix="select"
            isSearchable={true}
            name="color"
            options={sourceList}
            styles={colourStyles}
            placeholder="Выберите источник"
            value={sourceList.find((option) => option.value === String(formik.values.source)) || null}
            onChange={(arg) => {
              formik.setFieldValue("source", arg?.value);
            }}
          />
        </Flex>
      </ViewInformationBlock>
      <ViewInformationBlock icon={<Icon as={FiFile as any} boxSize={5} color="#3b515e" />} title="Дополнительная информация">
        <Flex flexDirection="column">
          <Text fontSize="0.875rem" marginBottom="0.5rem" color="#2D3748" fontWeight={500}>
            Комментарии
          </Text>
          <Textarea
            outline="1px solid black"
            bgColor="white"
            maxW="100%"
            id="comments"
            value={formik.values.comments}
            onChange={formik.handleChange}
          />
        </Flex>
        <BaseInput fieldTitle={"Ссылка"} name="url" value={formik.values.url} onChange={formik.handleChange} />
        <FileUploader
          onChangeDeletedIds={(ids) => formik.setFieldValue("attachments_delete", ids)}
          onChange={(files) => formik.setFieldValue("attachments", files)}
          currentFiles={currentFiles}
        />
      </ViewInformationBlock>
      <AddSkillsModal visible={isModalVisible} onClose={handleModalCancel} onConfirm={handleModalConfirm} newSkills={newSkills} />
    </Flex>
  );
});
